import React, { useState } from 'react';
import { useTheme } from '../../../../Context/ThemeContext';
import { PlayerHandler, PlayerHeaderInfo } from '../PlayerHandler';

interface AdjustRatingModalProps {
  header: PlayerHeaderInfo;
  onClose: () => void;
  onSuccess: () => void;
}

const rankThresholds = [
  { name: 'Beginner', min: 0, max: 299 },
  { name: 'Apprentice', min: 300, max: 599 },
  { name: 'Professional', min: 600, max: 899 },
  { name: 'Expert', min: 900, max: 1199 },
  { name: 'Master', min: 1200, max: 1499 },
  { name: 'GrandMaster', min: 1500, max: null },
];

const getRankForScore = (score: number): string => {
  for (const rank of rankThresholds) {
    if (rank.max === null || score <= rank.max) return rank.name;
  }
  return 'GrandMaster';
};

const AdjustRatingModal: React.FC<AdjustRatingModalProps> = ({ header, onClose, onSuccess }) => {
  const { theme } = useTheme();
  const [newScore, setNewScore] = useState<number>(header.rankScore ?? 0);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const predictedRank = getRankForScore(newScore);
  const currentRank = header.rankName || 'Beginner';
  const scoreDiff = newScore - (header.rankScore ?? 0);

  const handleSubmit = async () => {
    if (!header.userId) return;
    if (newScore < 0) {
      setError('Score cannot be negative');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const handler = new PlayerHandler();
      await handler.adjustPlayerRating(header.userId, newScore, reason);
      onSuccess();
    } catch (err) {
      setError('Failed to update rating');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const styles: Record<string, React.CSSProperties> = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
    modal: {
      background: theme.card,
      borderRadius: '12px',
      padding: '24px',
      width: '400px',
      maxWidth: '90vw',
      boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    },
    title: {
      fontSize: '18px',
      fontWeight: 700,
      color: theme.text,
      marginBottom: '4px',
    },
    subtitle: {
      fontSize: '13px',
      color: theme.textSecondary,
      marginBottom: '20px',
    },
    label: {
      fontSize: '13px',
      fontWeight: 600,
      color: theme.text,
      marginBottom: '6px',
      display: 'block',
    },
    input: {
      width: '100%',
      padding: '10px 12px',
      fontSize: '14px',
      border: `1px solid ${theme.inputBorder}`,
      borderRadius: '8px',
      background: theme.inputBg,
      color: theme.text,
      outline: 'none',
      marginBottom: '16px',
      boxSizing: 'border-box' as const,
    },
    preview: {
      padding: '12px',
      borderRadius: '8px',
      background: theme.inputBg,
      marginBottom: '16px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    previewLabel: {
      fontSize: '12px',
      color: theme.textSecondary,
    },
    previewValue: {
      fontSize: '14px',
      fontWeight: 600,
      color: theme.text,
    },
    diff: {
      fontSize: '12px',
      fontWeight: 600,
      color: scoreDiff > 0 ? '#22c55e' : scoreDiff < 0 ? '#ef4444' : theme.textSecondary,
    },
    rankChange: {
      fontSize: '13px',
      fontWeight: 600,
      color: predictedRank !== currentRank ? '#f59e0b' : theme.textSecondary,
    },
    error: {
      fontSize: '13px',
      color: '#ef4444',
      marginBottom: '12px',
    },
    actions: {
      display: 'flex',
      gap: '8px',
      justifyContent: 'flex-end',
    },
    cancelBtn: {
      padding: '8px 20px',
      fontSize: '13px',
      fontWeight: 500,
      border: `1px solid ${theme.border}`,
      borderRadius: '8px',
      cursor: 'pointer',
      background: 'transparent',
      color: theme.textSecondary,
    },
    submitBtn: {
      padding: '8px 20px',
      fontSize: '13px',
      fontWeight: 600,
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      background: '#3b82f6',
      color: '#fff',
      opacity: loading ? 0.6 : 1,
    },
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.title}>Adjust Player Rating</div>
        <div style={styles.subtitle}>
          {header.username} - Current: {header.rankScore} ({currentRank})
        </div>

        <label style={styles.label}>New Rank Score</label>
        <input
          style={styles.input}
          type="number"
          min={0}
          value={newScore}
          onChange={(e) => setNewScore(parseInt(e.target.value, 10) || 0)}
        />

        <div style={styles.preview}>
          <div>
            <div style={styles.previewLabel}>Score Change</div>
            <div style={styles.diff}>
              {scoreDiff > 0 ? '+' : ''}{scoreDiff}
            </div>
          </div>
          <div>
            <div style={styles.previewLabel}>New Rank</div>
            <div style={styles.rankChange}>{predictedRank}</div>
          </div>
        </div>

        <label style={styles.label}>Reason</label>
        <input
          style={styles.input}
          placeholder="Why are you adjusting this rating?"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />

        {error && <div style={styles.error}>{error}</div>}

        <div style={styles.actions}>
          <button style={styles.cancelBtn} onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button style={styles.submitBtn} onClick={handleSubmit} disabled={loading}>
            {loading ? 'Saving...' : 'Update Rating'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdjustRatingModal;
